import { type MRT_RowData, type MRT_TableOptions } from 'material-react-table';

/**
 * Mobile-optimized MRT options
 * These settings are applied when shouldUseMobileLayout is true
 */
export const getMobileMRTOptions = <TData extends MRT_RowData>(): Partial<
  MRT_TableOptions<TData>
> => ({
  // Simplified UI for mobile
  enableColumnOrdering: false,
  enableColumnDragging: false,
  enableColumnResizing: false,
  enableColumnPinning: false,
  enableMultiSort: false,

  // Compact density for space efficiency
  initialState: {
    density: 'compact',
  },

  // Touch-friendly cell styling
  muiTableHeadCellProps: {
    sx: {
      fontSize: '0.8rem',
      fontWeight: 600,
      py: 1.5, // Increased padding for touch targets
      px: 1,
    },
  },

  muiTableBodyCellProps: {
    sx: {
      fontSize: '0.75rem',
      py: 1.25, // Minimum 44px touch target height
      px: 0.75,
    },
  },

  // Mobile-friendly pagination
  muiPaginationProps: {
    rowsPerPageOptions: [25, 50, 100],
    showFirstButton: false,
    showLastButton: false,
  } as any,
});

export const getDefaultMRTOptions = <TData extends MRT_RowData>(): Partial<
  MRT_TableOptions<TData>
> => ({
  // Server-side data management
  manualFiltering: true,
  manualPagination: true,
  manualSorting: true,

  // Pagination
  paginationDisplayMode: 'pages',
  muiPaginationProps: {
    rowsPerPageOptions: [50, 100, 150, 200],
  } as any,

  // Column features
  enableColumnOrdering: true,
  enableColumnDragging: true,
  enableColumnResizing: true,
  enableColumnPinning: true,
  enableRowPinning: false,

  // Sorting - enable globally, columns can override
  enableSorting: true,
  enableSortingRemoval: true,
  enableMultiSort: true,

  // Filtering with faceted values
  enableFacetedValues: true,
  enableColumnFilters: true,
  columnFilterDisplayMode: 'popover',
  enableColumnFilterModes: true,

  // Filter UI customization
  muiFilterAutocompleteProps: () =>
    ({
      openOnFocus: true,
      disableCloseOnSelect: true,
    }) as any,
  muiFilterTextFieldProps: () =>
    ({
      autoFocus: true,
      size: 'small',
      sx: { minWidth: 320 },
    }) as any,
  muiSearchTextFieldProps: {
    placeholder: 'Search (vin:, ro:, part:, name:, Column: value)',
    size: 'small',
    // Only enable column suggestions after user starts typing
    inputProps: {
      id: 'afl-global-search',
    },
    onFocus: (e: any) => {
      const t = e?.target as HTMLInputElement | undefined;
      if (!t) return;
      if (!t.value) t.removeAttribute('list');
    },
    onInput: (e: any) => {
      const t = e?.target as HTMLInputElement | undefined;
      if (!t) return;
      const hasText = String(t.value || '').trim().length > 0;
      if (hasText) {
        t.setAttribute('list', 'afl-search-columns');
      } else {
        t.removeAttribute('list');
      }
    },
    onKeyDown: (e: any) => {
      // Allow TAB to accept the first matching column suggestion
      if (e?.key !== 'Tab') return;
      const t = e?.target as HTMLInputElement | undefined;
      if (!t) return;
      const current = String(t.value || '');
      // Do not interfere if the user is already using a known explicit prefix (vin:, ro:, part:)
      if (/^[^:]+:\s*/i.test(current) || /^\s*$/.test(current)) return;
      const list = document.getElementById('afl-search-columns') as HTMLDataListElement | null;
      if (!list) return;
      const norm = current.trim().toLowerCase();
      let match: string | undefined;
      for (const opt of Array.from(list.options)) {
        const v = String(opt.value || '');
        if (v.toLowerCase().startsWith(norm)) {
          match = v;
          break;
        }
      }
      if (match) {
        t.value = match + ' ';
        // Notify React/MRT about the value change
        const ev = new Event('input', { bubbles: true });
        t.dispatchEvent(ev);
        e.preventDefault();
      }
    },
  } as any,
  displayColumnDefOptions: {},
  muiTableBodyCellProps: ({ cell }) => ({
    title: String((cell.getValue?.() as any) ?? ''),
    sx: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  }),
  initialState: {
    showColumnFilters: true,
    density: 'compact',
  },
  muiTableHeadCellProps: {
    sx: { fontSize: '0.9rem' },
  },
  defaultColumn: {
    minSize: 60,
    size: 100,
    maxSize: 300,
  },
});
